import { clsx } from "clsx";
import { forwardRef, useEffect, useRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";
import { Loader2, X } from "lucide-react";

type BotonProps = ButtonHTMLAttributes<HTMLButtonElement> & { variante?: "primario" | "secundario" | "fantasma" | "peligro"; tamano?: "sm" | "md" | "lg"; cargando?: boolean };
export const Boton = forwardRef<HTMLButtonElement, BotonProps>(function Boton({ variante = "primario", tamano = "md", cargando, className, children, disabled, ...rest }, ref) {
  return (
    <button ref={ref} disabled={disabled || cargando}
      className={clsx("inline-flex items-center justify-center gap-2 rounded-md font-mono font-semibold tracking-tight transition-colors select-none disabled:opacity-50 disabled:cursor-not-allowed",
        tamano === "sm" && "h-9 px-3 text-[13px]", tamano === "md" && "h-11 px-4 text-sm", tamano === "lg" && "h-14 px-6 text-base",
        variante === "primario" && "bg-primary text-white hover:bg-primary-hover",
        variante === "secundario" && "bg-white text-tinta border border-borde hover:border-navy",
        variante === "fantasma" && "bg-transparent text-tinta hover:bg-gray-100",
        variante === "peligro" && "bg-error text-white hover:bg-red-600", className)} {...rest}>
      {cargando && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
      {children}
    </button>
  );
});

export function Campo({ etiqueta, ayuda, error, children, htmlFor }: { etiqueta: string; ayuda?: string; error?: string; children: ReactNode; htmlFor?: string }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="text-sm font-medium text-tinta">{etiqueta}</label>
      {children}
      {error ? <p className="text-sm text-error" role="alert">{error}</p> : ayuda ? <p className="text-sm text-tinta-suave">{ayuda}</p> : null}
    </div>
  );
}

const claseControl = "h-11 w-full rounded-md border border-borde bg-white px-3 text-base text-tinta placeholder:text-gray-400 focus:border-primary disabled:bg-gray-50";
export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input({ className, ...rest }, ref) {
  return <input ref={ref} className={clsx(claseControl, className)} {...rest} />;
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(function Select({ className, children, ...rest }, ref) {
  return <select ref={ref} className={clsx(claseControl, "pr-8", className)} {...rest}>{children}</select>;
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea({ className, ...rest }, ref) {
  return <textarea ref={ref} className={clsx("min-h-24 w-full rounded-md border border-borde bg-white p-3 text-base text-tinta placeholder:text-gray-400 focus:border-primary", className)} {...rest} />;
});

export function Badge({ tono = "neutro", children, className }: { tono?: "ok" | "alerta" | "error" | "neutro" | "info"; children: ReactNode; className?: string }) {
  return (
    <span className={clsx("inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap",
      tono === "ok" && "bg-emerald-50 text-emerald-800", tono === "alerta" && "bg-alerta-suave text-amber-900", tono === "error" && "bg-error-suave text-red-800",
      tono === "neutro" && "bg-gray-100 text-gray-700", tono === "info" && "bg-acero/40 text-navy", className)}>{children}</span>
  );
}

export function Tarjeta({ children, className, titulo, accion }: { children: ReactNode; className?: string; titulo?: string; accion?: ReactNode }) {
  return (
    <section className={clsx("rounded-md border border-borde bg-white", className)}>
      {(titulo || accion) && (
        <header className="flex items-center justify-between gap-3 border-b border-borde px-4 py-3">
          {titulo && <h2 className="text-base font-semibold">{titulo}</h2>}
          {accion}
        </header>
      )}
      <div className="p-4">{children}</div>
    </section>
  );
}

export function Cargando({ texto = "Cargando…" }: { texto?: string }) {
  return <div className="flex items-center gap-2 p-6 text-tinta-suave" role="status" aria-live="polite"><Loader2 className="h-5 w-5 animate-spin" aria-hidden /> {texto}</div>;
}

export function Esqueleto({ filas = 4 }: { filas?: number }) {
  return (
    <div className="animate-pulse space-y-3 p-4" aria-hidden>
      {Array.from({ length: filas }).map((_, i) => <div key={i} className="h-10 rounded bg-gray-100" />)}
    </div>
  );
}

export function Vacio({ titulo, texto, accion }: { titulo: string; texto?: string; accion?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
      <h3 className="text-lg font-semibold">{titulo}</h3>
      {texto && <p className="max-w-sm text-tinta-suave">{texto}</p>}
      {accion}
    </div>
  );
}

export function ErrorBloque({ mensaje, reintentar }: { mensaje: string; reintentar?: () => void }) {
  return (
    <div className="rounded-md border border-red-200 bg-error-suave p-4 text-red-900" role="alert">
      <p>{mensaje}</p>
      {reintentar && <Boton variante="secundario" tamano="sm" className="mt-3" onClick={reintentar}>Reintentar</Boton>}
    </div>
  );
}

/** Número grande con etiqueta; `nota` va chiquita abajo (p. ej. "el modelo muestra −3.97%"). Figuras proporcionales (no tabulares) en el valor grande. */
export function Numero({ etiqueta, valor, nota, tono, tamano = "md", extra, className }: { etiqueta: string; valor: string; nota?: ReactNode; tono?: "ok" | "alerta" | "error"; tamano?: "sm" | "md" | "hero"; extra?: ReactNode; className?: string }) {
  return (
    <div className={clsx("rounded-md border border-borde bg-white p-4", className)}>
      <div className="eyebrow text-tinta-suave">{etiqueta}</div>
      <div className={clsx("mt-1 font-semibold", tamano === "sm" && "text-lg", tamano === "md" && "text-2xl", tamano === "hero" && "titulo-serif text-[2.6rem] leading-tight md:text-6xl",
        tono === "alerta" && "text-amber-700", tono === "error" && "text-red-700", tono === "ok" && "text-emerald-700")}>{valor}</div>
      {extra && <div className="mt-2">{extra}</div>}
      {nota && <div className="mt-1 text-xs text-tinta-suave">{nota}</div>}
    </div>
  );
}

/** Diálogo nativo (<dialog>): foco atrapado y Esc por el navegador. */
export function Dialogo({ abierto, onCerrar, titulo, children, ancho = "md" }: { abierto: boolean; onCerrar: () => void; titulo: string; children: ReactNode; ancho?: "md" | "lg" }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (abierto && !d.open) d.showModal();
    if (!abierto && d.open) d.close();
  }, [abierto]);
  return (
    <dialog ref={ref} onClose={onCerrar} onClick={(e) => { if (e.target === ref.current) onCerrar(); }}
      className={clsx("m-auto w-[calc(100%-2rem)] rounded-lg border border-borde bg-white p-0 text-tinta shadow-xl backdrop:bg-black/40", ancho === "md" ? "max-w-lg" : "max-w-3xl")}>
      <div className="flex items-center justify-between border-b border-borde px-5 py-3">
        <h2 className="text-lg font-semibold">{titulo}</h2>
        <button type="button" onClick={onCerrar} aria-label="Cerrar" className="rounded p-2 hover:bg-gray-100"><X className="h-5 w-5" aria-hidden /></button>
      </div>
      <div className="max-h-[75vh] overflow-y-auto p-5">{children}</div>
    </dialog>
  );
}
